import * as THREE from "three";
import { COLUMN_COLORS, COLUMN_INK, COLUMNS, columnFor, type Column } from "./logic";

const ballCache = new Map<number, THREE.CanvasTexture>();
const matCache = new Map<number, THREE.MeshPhysicalMaterial>();
const faceCache = new Map<number, THREE.CanvasTexture>();
const faceMatCache = new Map<number, THREE.MeshBasicMaterial>();
let boardTexture: THREE.CanvasTexture | null = null;
let boardCanvas: HTMLCanvasElement | null = null;
let lastBoardKey = "";

const FONT = '"Arial Black","Segoe UI Black","Arial",system-ui,sans-serif';
const CREAM = "#f7f3ea";
const INK = "#14110e";
const shellCache = new Map<Column, THREE.MeshPhysicalMaterial>();

function makeCanvas(size: number) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  return canvas;
}

function shade(hex: string, amount: number) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, Math.round(((n >> 16) & 255) * amount));
  const g = Math.min(255, Math.round(((n >> 8) & 255) * amount));
  const b = Math.min(255, Math.round((n & 255) * amount));
  return `rgb(${r},${g},${b})`;
}

function letterOnCream(col: Column) {
  return col === "N" ? INK : COLUMN_COLORS[col];
}

function drawCallFace(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  num: number,
) {
  const col = columnFor(num);
  const band = COLUMN_COLORS[col];
  const digits = String(num);

  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = CREAM;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.9, 0, Math.PI * 2);
  ctx.strokeStyle = band;
  ctx.lineWidth = Math.max(10, r * 0.12);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.78, 0, Math.PI * 2);
  ctx.strokeStyle = shade(band, 1.15);
  ctx.lineWidth = Math.max(3, r * 0.035);
  ctx.stroke();

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.fillStyle = letterOnCream(col);
  ctx.font = `800 ${Math.round(r * 0.28)}px ${FONT}`;
  ctx.fillText(col, cx, cy - r * 0.38);

  ctx.fillStyle = INK;
  ctx.strokeStyle = "rgba(255,255,255,0.65)";
  ctx.lineWidth = Math.max(3, r * 0.04);
  const nSize = digits.length === 1 ? r * 0.92 : r * 0.78;
  ctx.font = `900 ${Math.round(nSize)}px ${FONT}`;
  ctx.strokeText(digits, cx, cy + r * 0.14);
  ctx.fillText(digits, cx, cy + r * 0.14);
}

function drawBandStamp(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  num: number,
) {
  const col = columnFor(num);
  const band = COLUMN_COLORS[col];
  const digits = String(num);

  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = CREAM;
  ctx.fill();
  ctx.lineWidth = Math.max(3, r * 0.1);
  ctx.strokeStyle = shade(band, 0.75);
  ctx.stroke();

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = letterOnCream(col);
  ctx.font = `800 ${Math.round(r * 0.3)}px ${FONT}`;
  ctx.fillText(col, cx, cy - r * 0.34);
  ctx.fillStyle = INK;
  ctx.font = `900 ${Math.round(digits.length === 1 ? r * 0.86 : r * 0.7)}px ${FONT}`;
  ctx.fillText(digits, cx, cy + r * 0.16);
}

export function getBallTexture(num: number): THREE.CanvasTexture {
  const cached = ballCache.get(num);
  if (cached) return cached;

  const size = 1024;
  const canvas = makeCanvas(size);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas");

  const col = columnFor(num);
  const band = COLUMN_COLORS[col];

  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, size, size);

  const bandY = size * 0.26;
  const bandH = size * 0.48;
  const grad = ctx.createLinearGradient(0, bandY, 0, bandY + bandH);
  grad.addColorStop(0, shade(band, 1.28));
  grad.addColorStop(0.45, band);
  grad.addColorStop(1, shade(band, 0.62));
  ctx.fillStyle = grad;
  ctx.fillRect(0, bandY, size, bandH);
  ctx.fillStyle = "rgba(255,255,255,0.28)";
  ctx.fillRect(0, bandY, size, 14);
  ctx.fillStyle = "rgba(0,0,0,0.12)";
  ctx.fillRect(0, bandY + bandH - 10, size, 10);

  const stampR = size * 0.11;
  const stampY = size * 0.5;
  for (let i = 0; i < 4; i++) {
    const stampX = ((i + 0.5) / 4) * size;
    drawBandStamp(ctx, stampX, stampY, stampR, num);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  ballCache.set(num, tex);
  return tex;
}

export function getBallMaterial(num: number): THREE.MeshPhysicalMaterial {
  const hit = matCache.get(num);
  if (hit) return hit;
  const mat = new THREE.MeshPhysicalMaterial({
    map: getBallTexture(num),
    roughness: 0.32,
    metalness: 0.02,
    clearcoat: 0.45,
    clearcoatRoughness: 0.28,
    color: "#ffffff",
  });
  matCache.set(num, mat);
  return mat;
}

export function getShellMaterial(num: number): THREE.MeshPhysicalMaterial {
  const col = columnFor(num);
  const hit = shellCache.get(col);
  if (hit) return hit;
  const band = COLUMN_COLORS[col];
  const mat = new THREE.MeshPhysicalMaterial({
    color: col === "N" ? "#e7e5e4" : band,
    roughness: 0.2,
    metalness: 0.06,
    clearcoat: 0.9,
    clearcoatRoughness: 0.16,
  });
  shellCache.set(col, mat);
  return mat;
}

export function getFaceTexture(num: number): THREE.CanvasTexture {
  const cached = faceCache.get(num);
  if (cached) return cached;

  const size = 1024;
  const canvas = makeCanvas(size);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas");

  ctx.clearRect(0, 0, size, size);
  drawCallFace(ctx, size / 2, size / 2, size / 2 - 8, num);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  faceCache.set(num, tex);
  return tex;
}

export function getFaceMaterial(num: number): THREE.MeshBasicMaterial {
  const hit = faceMatCache.get(num);
  if (hit) return hit;
  const mat = new THREE.MeshBasicMaterial({
    map: getFaceTexture(num),
    transparent: true,
    alphaTest: 0.08,
    depthWrite: true,
    toneMapped: false,
  });
  faceMatCache.set(num, mat);
  return mat;
}

export const BOARD_W = 2.08;
export const BOARD_H = 3.24;
export const BOARD_HEADER = 0.46;

export function wellLocal(col: number, row: number, scale: number, out: THREE.Vector3) {
  const padX = 0.13;
  const padBottom = 0.12;
  const x = (-BOARD_W / 2 + padX + ((col + 0.5) * (BOARD_W - padX * 2)) / 5) * scale;
  const gridH = BOARD_H - BOARD_HEADER - padBottom;
  const y = (BOARD_H / 2 - BOARD_HEADER - ((row + 0.5) * gridH) / 15) * scale;
  out.set(x, y, 0.09 * scale);
  return out;
}

export function getBoardTexture(called: number[], last: number | null): THREE.CanvasTexture {
  const key = `${called.join(",")}|${last ?? ""}`;
  if (boardTexture && key === lastBoardKey) return boardTexture;

  const w = 768;
  const h = 1200;
  if (!boardCanvas) {
    boardCanvas = document.createElement("canvas");
    boardCanvas.width = w;
    boardCanvas.height = h;
  }
  const ctx = boardCanvas.getContext("2d");
  if (!ctx) throw new Error("canvas");

  const wood = ctx.createLinearGradient(0, 0, w, h);
  wood.addColorStop(0, "#2a1810");
  wood.addColorStop(0.5, "#1a0f0c");
  wood.addColorStop(1, "#24140e");
  ctx.fillStyle = wood;
  ctx.fillRect(0, 0, w, h);

  ctx.strokeStyle = "rgba(196,165,116,0.45)";
  ctx.lineWidth = 10;
  ctx.strokeRect(8, 8, w - 16, h - 16);
  ctx.strokeStyle = "rgba(196,165,116,0.2)";
  ctx.lineWidth = 2;
  ctx.strokeRect(22, 22, w - 44, h - 44);

  const headerH = (BOARD_HEADER / BOARD_H) * h;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  COLUMNS.forEach((letter, i) => {
    const x = ((i + 0.5) / 5) * w;
    ctx.fillStyle = "#c4a574";
    ctx.font = `700 ${Math.floor(headerH * 0.62)}px "Palatino Linotype", Palatino, Georgia, serif`;
    ctx.fillText(letter, x, headerH * 0.52);
  });

  const calledSet = new Set(called);
  const padX = 0.13 / BOARD_W;
  const padBottom = 0.12 / BOARD_H;
  const headerFrac = BOARD_HEADER / BOARD_H;

  for (let col = 0; col < 5; col++) {
    for (let row = 0; row < 15; row++) {
      const num = col * 15 + row + 1;
      const u = padX + ((col + 0.5) * (1 - padX * 2)) / 5;
      const gridH = 1 - headerFrac - padBottom;
      const vTop = headerFrac + ((row + 0.5) * gridH) / 15;
      const x = u * w;
      const y = vTop * h;
      const r = Math.min(w / 5, (h - headerH) / 15) * 0.36;
      const letter = COLUMNS[col] as Column;
      const isCalled = calledSet.has(num);
      const isLast = last === num;

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      if (isCalled) {
        ctx.fillStyle = COLUMN_COLORS[letter];
        ctx.fill();
        if (isLast) {
          ctx.lineWidth = 4;
          ctx.strokeStyle = "#f4e8d0";
          ctx.stroke();
        }
      } else {
        ctx.fillStyle = "rgba(8,6,5,0.72)";
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = "rgba(196,165,116,0.28)";
        ctx.stroke();
      }

      ctx.fillStyle = isCalled ? COLUMN_INK[letter] : "#cbbba4";
      ctx.font = `700 ${Math.floor(r * 0.95)}px "Segoe UI", system-ui, sans-serif`;
      ctx.fillText(String(num), x, y + 1);
    }
  }

  if (!boardTexture) {
    boardTexture = new THREE.CanvasTexture(boardCanvas);
    boardTexture.colorSpace = THREE.SRGBColorSpace;
    boardTexture.anisotropy = 8;
  } else {
    boardTexture.needsUpdate = true;
  }
  lastBoardKey = key;
  return boardTexture;
}

export function disposeTextures() {
  ballCache.forEach((t) => t.dispose());
  ballCache.clear();
  matCache.forEach((m) => m.dispose());
  matCache.clear();
  faceCache.forEach((t) => t.dispose());
  faceCache.clear();
  faceMatCache.forEach((m) => m.dispose());
  faceMatCache.clear();
  shellCache.forEach((m) => m.dispose());
  shellCache.clear();
  boardTexture?.dispose();
  boardTexture = null;
  boardCanvas = null;
  lastBoardKey = "";
}
